import style from "./style.scss";

import { Fragment } from "preact";
import { useState, useRef, useEffect, useId } from "preact/hooks";
import Link, { Button, ButtonList } from "../../../../components/link";
import Textbox from "../../../../components/textbox";
import { usePopup } from "../../../../components/popup/context";
import Popup from "../../../../components/popup";
import NoWrap from "../../../../components/no-wrap";
import VoltageRangeDisplay from "../voltage-range-display";
import PopupContent from "./popupContent";
import { mergeClasses } from "../../../../components/helpers";

const isNum = (v) => {
  return v.trim() !== "" && !isNaN(v) && isFinite(v);
};

enum ValidationStatus {
  Incomplete = -1,
  Invalid = 0,
  Valid = 1,
}

enum CalculationStatus {
  Incomplete = -1,
  Incompatible = 0,
  Supported = 1,
}

const getValueDefaults = () => ({
  typed: { minVolts: "", maxVolts: "", maxAmps: "", maxWatts: "" },
  parsed: { minVolts: 0, maxVolts: 0, maxAmps: 0, maxWatts: 0 },
});

const getValidationDefaults = () => ({
  status: ValidationStatus.Incomplete,
  errors: {
    minVolts: false,
    maxVolts: false,
    maxAmps: false,
    maxWatts: false,
    voltsRange: false,
    warnIncomplete: false,
  },
});

const getCalculationDefaults = () => ({
  status: CalculationStatus.Incomplete,
  warnings: {
    low: false,
    high: false,
  },
  results: {},
});

const InputCalculator = ({
  minChargingVolts, //TODO: Perhaps warn user about something?
  highestMinChargingVolts,
  lowestMaxChargingVolts,
  maxChargingVolts,
  minChargingAmps = 0, //TODO: I don't think minimum amps matters
  maxChargingAmps, //TODO: Plus1K might be be 20A, but might only be for XT150 cable
  chargerProfiles,
  highWarning,
}) => {
  //TODO: Use refs to manage max-height animation (see accordion)
  const formRef = useRef(null);
  const aaaaRef = useRef(null);
  const bbbbRef = useRef(null);
  const ccccRef = useRef(null);

  const [values, setValues] = useState(getValueDefaults());
  const [validation, setValidation] = useState(getValidationDefaults());
  const [calculation, setCalculation] = useState(getCalculationDefaults());

  const popup = usePopup();
  const modalTitleId = useId();

  const openDetails = (e) => {
    e.preventDefault();

    popup.showPopup(
      <Popup ariaLabelledBy={modalTitleId}>
        <PopupContent titleId={modalTitleId} />
      </Popup>
    );
  };

  const onInput = (key) => (e) => {
    const rawValue = e.target.value;

    // Strip everything but numbers and dots
    let sanitized = rawValue.replace(/[^0-9.]/g, "");

    // Handle multiple dots (12.5.5 becomes 12.55)
    const parts = sanitized.split(".");
    if (parts.length > 2) {
      sanitized = `${parts[0]}.${parts.slice(1).join("")}`;
    }

    setValues((prev) => {
      const nextValues = {
        typed: {
          ...prev.typed,
          [key]: sanitized, // This forces the textbox to be valid
        },
        parsed: {
          ...prev.parsed,
          [key]: sanitized === "" ? 0 : parseFloat(sanitized) || 0, // Ensure we have a valid number
        },
      };

      setValidation(validate(nextValues));
      setCalculation(getCalculationDefaults());

      return nextValues;
    });
  };

  const resetClick = (e) => {
    e.preventDefault();

    setValues(getValueDefaults());
    setValidation(getValidationDefaults());
    setCalculation(getCalculationDefaults());
  };

  const calculateClick = (e) => {
    e.preventDefault();

    if (validation.status == ValidationStatus.Valid) {
      const calculationResult = calculate(values, validation);
      setCalculation(calculationResult);
    } else {
      setValidation((prev) => ({
        ...prev,
        errors: { ...prev.errors, warnIncomplete: true },
      }));
    }
  };

  const validate = (newValues) => {
    let result = getValidationDefaults();

    if (
      isNum(newValues.typed.minVolts) &&
      isNum(newValues.typed.maxVolts) &&
      isNum(newValues.typed.maxAmps) &&
      isNum(newValues.typed.maxWatts)
    ) {
      if (
        newValues.parsed.minVolts <= 0 ||
        newValues.parsed.maxVolts <= 0 ||
        newValues.parsed.maxAmps <= 0 ||
        newValues.parsed.maxWatts <= 0 ||
        newValues.parsed.minVolts > newValues.parsed.maxVolts
      ) {
        result.status = ValidationStatus.Invalid;
        if (newValues.parsed.minVolts <= 0) {
          result.errors.minVolts = true;
        }
        if (newValues.parsed.maxVolts <= 0) {
          result.errors.maxVolts = true;
        }
        if (newValues.parsed.maxAmps <= 0) {
          result.errors.maxAmps = true;
        }
        if (newValues.parsed.maxWatts <= 0) {
          result.errors.maxWatts = true;
        }
        if (newValues.parsed.minVolts > newValues.parsed.maxVolts) {
          result.errors.voltsRange = true;
        }
      } else {
        result.status = ValidationStatus.Valid;
      }
    }

    return result;
  };

  const calculate = (currentValues, currentValidation) => {
    let result = getCalculationDefaults();
    // let warnMinVolts = false;
    // let warnMaxVolts = false;

    if (currentValidation.status == ValidationStatus.Valid) {
      if (
        lowestMaxChargingVolts > currentValues.parsed.maxVolts || // Alternator charger's minimum output volts are too high for port
        maxChargingVolts < currentValues.parsed.minVolts || // Alternator charger can't output minimum volts needed for port
        minChargingAmps > currentValues.parsed.maxAmps // Alternator charger's minimum amps are too high for port
      ) {
        // Specs are not supported
        result.status = CalculationStatus.Incompatible;
      } else {
        result.status = CalculationStatus.Supported;

        // if (minChargingVolts < minVolts) {
        //   warnMinVolts = true; //TODO: Warn user to adjust volts
        // }

        // if (maxChargingVolts > maxVolts) {
        //   warnMaxVolts = true; //TODO: Warn user to adjust volts
        // }

        if (currentValues.parsed.minVolts > highWarning) {
          result.warnings.high = true;
        }

        // Limit amps to what alternator charger will output
        let amps = currentValues.parsed.maxAmps;
        if (maxChargingAmps <= currentValues.parsed.maxAmps) {
          amps = maxChargingAmps;
        }

        // 1. Calculate base power based on Volts * Amps
        let baseCalculatedWatts =
          Math.min(currentValues.parsed.maxVolts, maxChargingVolts) * amps;

        // 2. Limit to max watts allowed by the solar input port
        if (baseCalculatedWatts > currentValues.parsed.maxWatts) {
          baseCalculatedWatts = currentValues.parsed.maxWatts;
        }

        // 3. Limit to the specific hardware max wattage of each charger profile
        Object.keys(chargerProfiles).forEach((chargerName) => {
          const profile = chargerProfiles[chargerName];
          result.results[chargerName] = Math.round(
            Math.min(baseCalculatedWatts, profile.maxWatts)
          );
        });
      }
    }

    return result;
  };

  useEffect(() => {
    if (
      calculation.status !== CalculationStatus.Incomplete &&
      formRef.current &&
      aaaaRef.current &&
      bbbbRef.current
    ) {
      window.requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;
        const scrollY = window.pageYOffset;

        // 1. Get the bounding boxes
        const formRect = formRef.current.getBoundingClientRect();
        const aaaaRect = aaaaRef.current.getBoundingClientRect();
        const bbbbRect = bbbbRef.current.getBoundingClientRect();

        // 2. Find the lowest point of the two results containers
        // (Using Math.max because a larger 'bottom' value is lower on the screen)
        const lowestPoint = Math.max(aaaaRect.bottom, bbbbRect.bottom);

        // 3. Check if that lowest point is off-screen
        const isContentHidden = lowestPoint > viewportHeight - 20;

        if (isContentHidden) {
          // How much do we need to scroll to bring the bottom into view?
          const distanceToReveal = lowestPoint - viewportHeight + 40;
          const idealScrollPos = scrollY + distanceToReveal;

          // Your "Anchor": The scroll position where the form hits the top
          const maxScrollPos = formRect.top + scrollY - 6;

          // Scroll to show the bottom, but CAP it at the top of the form
          window.scrollTo({
            top: Math.min(idealScrollPos, maxScrollPos),
            behavior: "smooth",
          });
        }
      });
    }
  }, [calculation.status]);

  let minVoltageRangeDisplay = highestMinChargingVolts;
  if (calculation.status == CalculationStatus.Supported) {
    minVoltageRangeDisplay = Math.ceil(values.parsed.minVolts);
  } else if (
    calculation.status == CalculationStatus.Incompatible &&
    lowestMaxChargingVolts > values.parsed.maxVolts
  ) {
    minVoltageRangeDisplay = minChargingVolts;
  }

  let maxVoltageRangeDisplay = lowestMaxChargingVolts;
  if (calculation.status == CalculationStatus.Supported) {
    maxVoltageRangeDisplay = Math.floor(values.parsed.maxVolts);
  } else if (
    calculation.status == CalculationStatus.Incompatible &&
    maxChargingVolts < values.parsed.minVolts
  ) {
    maxVoltageRangeDisplay = maxChargingVolts;
  }

  return (
    <div class={style.calculatorWrapper}>
      <h3 class={style.title}>
        What are the specifications for your solar input port?
      </h3>
      <p class="caption-left">
        Need help finding these values?{" "}
        <Link href="#" onClick={(e) => openDetails(e)}>
          Show details
        </Link>
      </p>

      <div class={style.calculator}>
        <form ref={formRef}>
          <div class={style.textInputs}>
            <Textbox
              label="Min Volts"
              value={values.typed.minVolts}
              suffix="V"
              min={0}
              max={500}
              placeholder="0"
              type="text"
              inputmode="decimal"
              onInput={onInput("minVolts")}
            />
            <Textbox
              label="Max Volts"
              value={values.typed.maxVolts}
              suffix="V"
              min={0}
              max={500}
              placeholder="0"
              type="text"
              inputmode="decimal"
              onInput={onInput("maxVolts")}
            />
            <Textbox
              label="Max Amps"
              value={values.typed.maxAmps}
              suffix="A"
              min={0}
              max={500}
              placeholder="0"
              type="text"
              inputmode="decimal"
              onInput={onInput("maxAmps")}
            />
            <Textbox
              label="Max Watts"
              value={values.typed.maxWatts}
              suffix="W"
              min={0}
              max={5000}
              placeholder="0"
              type="text"
              inputmode="decimal"
              onInput={onInput("maxWatts")}
            />
          </div>

          <p class={style.message} aria-live="polite">
            {validation.status === ValidationStatus.Incomplete ? (
              <span
                class={validation.errors.warnIncomplete ? style.invalid : null}
              >
                Enter all values to calculate compatibility and expected charge
                rate.
              </span>
            ) : (
              <Fragment>
                {validation.status === ValidationStatus.Invalid ? (
                  <strong class={style.invalid}>Invalid Entry: </strong>
                ) : (
                  <span>You provided: </span>
                )}
                <mark>
                  <span
                    class={validation.errors.voltsRange ? style.invalid : null}
                  >
                    <span
                      class={validation.errors.minVolts ? style.invalid : null}
                    >
                      {values.typed.minVolts}
                    </span>
                    -
                    <span
                      class={validation.errors.maxVolts ? style.invalid : null}
                    >
                      {values.typed.maxVolts}
                    </span>
                  </span>
                  V⎓
                  <span
                    class={validation.errors.maxAmps ? style.invalid : null}
                  >
                    {values.typed.maxAmps}
                  </span>
                  A,{" "}
                  <span
                    class={validation.errors.maxWatts ? style.invalid : null}
                  >
                    {values.typed.maxWatts}
                  </span>
                  W
                </mark>
                . Values should match unit specs, though formatting may vary.{" "}
                <NoWrap>
                  <Link href="#" onClick={(e) => openDetails(e)}>
                    Show info
                  </Link>
                </NoWrap>
              </Fragment>
            )}
          </p>

          <ButtonList class={style.buttons}>
            {calculation.status == CalculationStatus.Incomplete ? (
              <Button type="submit" onClick={(e) => calculateClick(e)}>
                Calculate Compatibility
              </Button>
            ) : (
              <Button type="reset" onClick={(e) => resetClick(e)}>
                Reset Values
              </Button>
            )}
          </ButtonList>

          {calculation.status != CalculationStatus.Incomplete && (
            <div ref={aaaaRef} class={style.compatibility}>
              {calculation.status == CalculationStatus.Incompatible && (
                <div>
                  <strong class={style.notSupported}>Incompatible</strong>
                  <p class={style.unsupportedReason}>
                    {lowestMaxChargingVolts > values.parsed.maxVolts && (
                      <Fragment>
                        Charger requires a power station that can accept at
                        least {lowestMaxChargingVolts}V. Your unit’s maximum
                        input is only{" "}
                        <span class={style.invalid}>
                          {values.parsed.maxVolts}V
                        </span>
                        .
                      </Fragment>
                    )}
                    {maxChargingVolts < values.parsed.minVolts && (
                      <Fragment>
                        Charger requires a power station that can start charging
                        at {maxChargingVolts}V or less. Your unit’s minimum
                        input is{" "}
                        <span class={style.invalid}>
                          {values.parsed.minVolts}V
                        </span>
                        .
                      </Fragment>
                    )}
                  </p>
                </div>
              )}
              {calculation.status == CalculationStatus.Supported && (
                <div>
                  <strong class={style.supported}>
                    Your Supported Charge Rates
                  </strong>
                  <p class={style.supportModel}>
                    {Object.entries(calculation.results).map(([name, watt]) => (
                      <span key={name}>
                        <strong>{watt}W</strong>
                        <span> = {name} </span>
                      </span>
                    ))}
                  </p>
                </div>
              )}
            </div>
          )}

          {calculation.warnings.high && (
            <p class={style.warning}>
              <strong>Notice:</strong> Your{" "}
              <strong>{values.parsed.minVolts}V</strong> minimum is above the
              charger’s adjustable minimum range limit of{" "}
              <strong>{highestMinChargingVolts}V</strong> and will stop if
              voltage drops between{" "}
              <strong>
                {highestMinChargingVolts}-{values.parsed.minVolts}V
              </strong>
              .
            </p>
          )}
        </form>

        <div
          ref={bbbbRef}
          class={mergeClasses([
            style.appSettings,
            calculation.status == CalculationStatus.Incomplete
              ? style.disabled
              : null,
          ])}
        >
          <VoltageRangeDisplay
            min={minVoltageRangeDisplay}
            max={maxVoltageRangeDisplay}
            range={{
              min: minChargingVolts,
              highestMin: highestMinChargingVolts,
              lowestMax: lowestMaxChargingVolts,
              max: maxChargingVolts,
            }}
            tooLow={
              calculation.status == CalculationStatus.Incompatible &&
              maxChargingVolts < values.parsed.minVolts
            }
            tooHigh={
              calculation.status == CalculationStatus.Incompatible &&
              lowestMaxChargingVolts > values.parsed.maxVolts
            }
          />
          {calculation.status == CalculationStatus.Supported ? (
            <p ref={ccccRef} class={style.instructions}>
              <strong>EcoFlow app settings to use:</strong>
              <br />
              <span>
                Go to <em>Settings</em> &gt; <em>Battery type</em> &gt;{" "}
                <em>Power station</em>.
              </span>
            </p>
          ) : null}
        </div>
      </div>

      {/* <p class="caption" style="margin-top:18px;">
        * Calculation applies to power stations using XT60 output cable and
        third-party mode.
      </p> */}
    </div>
  );
};

export default InputCalculator;
